import { extractHubErrorFromPayload, HubApiError } from './hubApiError';
import { missionStartSubscriptionRequiredMessage } from './trialDossierMissionGate';
import { __ } from '../i18n/vsme';
import { formatMissingMissionDocuments } from './missionDocumentPolicy';
import type { MissionCollectionLink } from '../fileStore/fileStoreTypes';
import {
  vsmeMissionsListPath,
  vsmeMissionNewPath,
  vsmeMissionSetupPath,
} from './vsmePortalRoutes';

export type MissionDocumentType =
  | 'letter_of_mission'
  | 'acceptance_questionnaire'
  | 'aml_questionnaire';

export type MissionDocumentRow = {
  document_type: MissionDocumentType;
  attachment_id: number;
  file_name: string;
  mime_type: string;
};

/** `file_collection` is client-side only: never persisted, never returned by `resolveInitialWizardStep`. */
export type MissionWizardStep = 'company' | 'file_collection' | 'acceptation' | 'compliance';

export type MissionSummaryPayload = {
  mission: {
    id: number;
    company_id: number;
    subject_type?: string;
    tool_slug: string;
    title: string;
    status: string;
    period_start: string;
    period_end: string;
    responsable_user_id?: number | null;
    responsable_display_name?: string | null;
    current_step_key: string | null;
    archived_at?: string | null;
    can_continue?: boolean;
  };
  linked_report_id: number | null;
  documents?: MissionDocumentRow[];
  missing_required_documents?: MissionDocumentType[];
  required_document_types?: MissionDocumentType[];
  publish_catalog?: boolean;
};

export type MissionStartPayload = MissionSummaryPayload & {
  mission_url?: string;
  existing_mission?: boolean;
};

export type DiligencePackSuggestion = {
  pack_id: number;
  name: string;
  target_mode: 'all' | 'restricted';
  selected: boolean;
  locked: boolean;
  applicable: boolean;
  item_count?: number;
};

export type ResponsableOption = {
  id: number;
  display_name: string;
  role: string;
};

export type MissionApiConfig = {
  hubBaseUrl: string;
  vsmeBaseUrl: string;
  nonce: string;
  /** The company file store is switched on: the wizard offers the Collecte step. Absent = legacy. */
  fileStoreLive?: boolean;
};

export type MissionCollectionSlot = {
  type_key: string;
  label: string;
  files: MissionCollectionLink[];
};

/** `GET missions/{id}/file-collection` — a raw payload, not the hub envelope. */
export type MissionFileCollection = {
  slots: MissionCollectionSlot[];
  suggest_years: number[];
  owner_record_id: number;
};

/**
 * `importing`: another request is reading the file now (acchub #931). `queued`: the read did not
 * fit the request's time budget and runs in a background job (#1145). Both leave the read pending.
 */
export type MissionCollectionImportStatus = 'imported' | 'failed' | 'skipped_gate_off' | 'importing' | 'queued';

/** The FEC/DSN read run by the link request, one per linked file that has a profile artifact. */
export type MissionCollectionImport = {
  artifact_id: number;
  status: MissionCollectionImportStatus;
  error?: string;
};

/** `POST missions/{id}/file-collection/{type}/links` — the links just made, and their reads. */
export type MissionCollectionLinkResult = {
  files: MissionCollectionLink[];
  imports: MissionCollectionImport[];
};

const normalizeBase = (url: string): string => (url.endsWith('/') ? url : `${url}/`);

const parseHubEnvelope = <T,>(raw: unknown): T => {
  if (typeof raw !== 'object' || raw === null || !('success' in raw) || raw.success !== true || !('data' in raw)) {
    throw new Error(__('Réponse mission invalide.'));
  }
  return (raw as { data: T }).data;
};

const parseVsmeEnvelope = <T,>(raw: unknown): T => {
  if (typeof raw !== 'object' || raw === null) {
    throw new Error(__('Réponse VS invalide.'));
  }
  const payload = raw as { success?: boolean; data?: T };
  if (payload.success === true && payload.data !== undefined) {
    return payload.data;
  }
  if ('data' in payload && payload.data !== undefined) {
    return payload.data as T;
  }
  return raw as T;
};

type HubRestErrorPayload = {
  message?: string;
  code?: string;
  data?: {
    message?: string;
    missing?: MissionDocumentType[];
  };
};

const readErrorMessage = async (response: Response, fallback: string): Promise<string> => {
  const raw = await response.json().catch(() => null) as HubRestErrorPayload | null;
  if (raw === null) {
    return fallback;
  }

  if (raw.code === 'required_documents_missing') {
    const missing = raw.data?.missing;
    if (Array.isArray(missing) && missing.length > 0) {
      return formatMissingMissionDocuments(missing);
    }
  }

  if (typeof raw.message === 'string' && raw.message !== '') {
    return raw.message;
  }

  if (typeof raw.data?.message === 'string' && raw.data.message !== '') {
    return raw.data.message;
  }

  return fallback;
};

export const startMissionForCompany = async (
  config: MissionApiConfig,
  companyId: number,
): Promise<MissionStartPayload> => {
  const response = await fetch(`${normalizeBase(config.hubBaseUrl)}company/${companyId}/missions`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-WP-Nonce': config.nonce,
    },
    body: JSON.stringify({ tool_slug: 'vsme' }),
  });

  const raw: unknown = await response.json().catch(() => ({}));
  if (!response.ok) {
    const fallback = missionStartSubscriptionRequiredMessage();
    const error = extractHubErrorFromPayload(raw, response.status, fallback);
    if (error.code === 'trial.dossier_mission_denied') {
      throw new HubApiError(fallback, error.code, error.status);
    }
    throw error;
  }

  return parseHubEnvelope<MissionStartPayload>(raw);
};

export const startCabinetMission = async (
  config: MissionApiConfig,
): Promise<MissionStartPayload> => {
  const response = await fetch(`${normalizeBase(config.hubBaseUrl)}cabinet/missions`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-WP-Nonce': config.nonce,
    },
    body: JSON.stringify({ tool_slug: 'vsme' }),
  });

  const raw: unknown = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw extractHubErrorFromPayload(
      raw,
      response.status,
      __('Impossible de démarrer la mission cabinet.'),
    );
  }

  return parseHubEnvelope<MissionStartPayload>(raw);
};

export const fetchMissionSummary = async (
  config: MissionApiConfig,
  missionId: number,
): Promise<MissionSummaryPayload> => {
  const response = await fetch(`${normalizeBase(config.hubBaseUrl)}missions/${missionId}`, {
    credentials: 'same-origin',
    headers: { 'X-WP-Nonce': config.nonce },
  });
  const raw: unknown = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, __('Impossible de charger la mission.')));
  }
  return parseHubEnvelope<MissionSummaryPayload>(raw);
};

export const updateMissionSetup = async (
  config: MissionApiConfig,
  missionId: number,
  body: { current_step_key?: string | null; publish_catalog?: boolean; responsable_user_id?: number },
): Promise<MissionSummaryPayload> => {
  const response = await fetch(`${normalizeBase(config.hubBaseUrl)}missions/${missionId}/setup`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-WP-Nonce': config.nonce,
    },
    body: JSON.stringify(body),
  });
  const raw: unknown = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, __('Impossible d’enregistrer la mission.')));
  }
  return parseHubEnvelope<MissionSummaryPayload>(raw);
};

export const fetchMissionResponsables = async (
  config: MissionApiConfig,
  missionId: number,
): Promise<{ items: ResponsableOption[] }> => {
  const response = await fetch(`${normalizeBase(config.hubBaseUrl)}missions/${missionId}/responsables`, {
    credentials: 'same-origin',
    headers: { 'X-WP-Nonce': config.nonce },
  });
  const raw: unknown = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, __('Impossible de charger les responsables de mission.')));
  }
  return parseHubEnvelope<{ items: ResponsableOption[] }>(raw);
};

export const uploadMissionDocument = async (
  config: MissionApiConfig,
  missionId: number,
  documentType: MissionDocumentType,
  file: File,
): Promise<MissionDocumentRow> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('document_type', documentType);

  const response = await fetch(`${normalizeBase(config.hubBaseUrl)}missions/${missionId}/documents`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'X-WP-Nonce': config.nonce },
    body: formData,
  });
  const raw: unknown = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, __('Impossible de déposer le fichier.')));
  }
  const data = parseHubEnvelope<{ document: MissionDocumentRow }>(raw);
  return data.document;
};

const isMissionFileCollection = (raw: unknown): raw is MissionFileCollection => typeof raw === 'object'
  && raw !== null
  && 'slots' in raw
  && Array.isArray(raw.slots)
  && 'suggest_years' in raw
  && Array.isArray(raw.suggest_years)
  && 'owner_record_id' in raw
  && typeof raw.owner_record_id === 'number';

const isMissionCollectionLinkResult = (raw: unknown): raw is MissionCollectionLinkResult => typeof raw === 'object'
  && raw !== null
  && 'files' in raw
  && Array.isArray(raw.files)
  && 'imports' in raw
  && Array.isArray(raw.imports);

export const fetchMissionFileCollection = async (
  config: MissionApiConfig,
  missionId: number,
): Promise<MissionFileCollection> => {
  const response = await fetch(`${normalizeBase(config.hubBaseUrl)}missions/${missionId}/file-collection`, {
    credentials: 'same-origin',
    headers: { 'X-WP-Nonce': config.nonce },
  });
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, __('Impossible de charger la collecte de fichiers.')));
  }
  const raw: unknown = await response.json().catch(() => null);
  if (!isMissionFileCollection(raw)) {
    throw new Error(__('Réponse mission invalide.'));
  }
  return raw;
};

export const linkMissionCollectionFiles = async (
  config: MissionApiConfig,
  missionId: number,
  typeKey: string,
  storedFileIds: number[],
): Promise<MissionCollectionLinkResult> => {
  const response = await fetch(
    `${normalizeBase(config.hubBaseUrl)}missions/${missionId}/file-collection/${encodeURIComponent(typeKey)}/links`,
    {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-WP-Nonce': config.nonce,
      },
      body: JSON.stringify({ stored_file_ids: storedFileIds }),
    },
  );
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, __('Impossible d’ajouter les fichiers.')));
  }
  const raw: unknown = await response.json().catch(() => null);
  if (!isMissionCollectionLinkResult(raw)) {
    throw new Error(__('Réponse mission invalide.'));
  }
  return raw;
};

export const detachMissionCollectionLink = async (
  config: MissionApiConfig,
  missionId: number,
  linkId: number,
): Promise<{ detached: boolean }> => {
  const response = await fetch(
    `${normalizeBase(config.hubBaseUrl)}missions/${missionId}/file-collection/links/${linkId}`,
    {
      method: 'DELETE',
      credentials: 'same-origin',
      headers: { 'X-WP-Nonce': config.nonce },
    },
  );
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, __('Impossible de retirer le fichier.')));
  }
  return { detached: true };
};

export const downloadMissionDocumentBlob = async (
  config: MissionApiConfig,
  missionId: number,
  documentType: MissionDocumentType,
): Promise<Blob> => {
  const response = await fetch(
    `${normalizeBase(config.hubBaseUrl)}missions/${missionId}/documents/${documentType}/download`,
    {
      credentials: 'same-origin',
      headers: { 'X-WP-Nonce': config.nonce },
    },
  );
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, __('Impossible d’ouvrir le fichier.')));
  }
  return response.blob();
};

export const deleteMissionDocument = async (
  config: MissionApiConfig,
  missionId: number,
  documentType: MissionDocumentType,
): Promise<void> => {
  const response = await fetch(
    `${normalizeBase(config.hubBaseUrl)}missions/${missionId}/documents/${documentType}`,
    {
      method: 'DELETE',
      credentials: 'same-origin',
      headers: { 'X-WP-Nonce': config.nonce },
    },
  );
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, __('Impossible de supprimer le fichier.')));
  }
};

export const fetchMissionDiligencePacks = async (
  config: MissionApiConfig,
  missionId: number,
): Promise<{ suggestions: DiligencePackSuggestion[] }> => {
  const response = await fetch(`${normalizeBase(config.vsmeBaseUrl)}missions/${missionId}/diligence-packs`, {
    credentials: 'same-origin',
    headers: { 'X-WP-Nonce': config.nonce },
  });
  const raw: unknown = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, __('Impossible de charger les packs de diligences.')));
  }
  return parseVsmeEnvelope<{ suggestions: DiligencePackSuggestion[] }>(raw);
};

export const saveMissionDiligencePacks = async (
  config: MissionApiConfig,
  missionId: number,
  packIds: number[],
): Promise<void> => {
  const response = await fetch(`${normalizeBase(config.vsmeBaseUrl)}missions/${missionId}/diligence-packs`, {
    method: 'PUT',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-WP-Nonce': config.nonce,
    },
    body: JSON.stringify({ pack_ids: packIds }),
  });
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, __('Impossible d’enregistrer les packs de diligences.')));
  }
};

export const createMissionReport = async (
  config: MissionApiConfig,
  companyId: number,
  missionId: number,
  fiscalYearStart: string,
  fiscalYearEnd: string,
): Promise<{ id: string | number }> => {
  const response = await fetch(`${normalizeBase(config.vsmeBaseUrl)}reports`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-WP-Nonce': config.nonce,
    },
    body: JSON.stringify({
      company_id: companyId,
      fiscal_year_start: fiscalYearStart,
      fiscal_year_end: fiscalYearEnd,
      mission_id: missionId,
    }),
  });
  const raw: unknown = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, __('Impossible de créer le rapport VS.')));
  }
  return parseVsmeEnvelope<{ id: string | number }>(raw);
};

export const resolveInitialWizardStep = (summary: MissionSummaryPayload): MissionWizardStep => {
  if (summary.linked_report_id !== null) {
    return 'compliance';
  }
  const missing = summary.missing_required_documents ?? [];
  const step = summary.mission.current_step_key;
  if (missing.length > 0 || step === 'acceptation' || step === null) {
    return 'acceptation';
  }
  if (step === 'compliance') {
    return 'compliance';
  }
  return 'acceptation';
};

export const buildMissionWizardUrl = (params: { companyId?: number; missionId?: number }): string => {
  if (params.missionId !== undefined && params.missionId > 0) {
    return vsmeMissionSetupPath(params.missionId);
  }
  return vsmeMissionNewPath(params.companyId);
};

/** Landing after save-and-quit, never re-open the setup wizard via query flags. */
export const buildMissionQuitUrl = (portalBasePath = '/hub'): string =>
  vsmeMissionsListPath(portalBasePath);

export const resolvePortalBasePath = (): string => {
  const dashboardAdmin = (window as { VSMEDashboardAdmin?: { portalBasePath?: string } }).VSMEDashboardAdmin;
  if (typeof dashboardAdmin?.portalBasePath === 'string' && dashboardAdmin.portalBasePath !== '') {
    return dashboardAdmin.portalBasePath;
  }
  const reportsAdmin = (window as { VSMEReportsAdmin?: { portalBasePath?: string } }).VSMEReportsAdmin;
  if (typeof reportsAdmin?.portalBasePath === 'string' && reportsAdmin.portalBasePath !== '') {
    return reportsAdmin.portalBasePath;
  }
  const vsme = (window as { VSME?: { portalUrl?: string } }).VSME;
  if (typeof vsme?.portalUrl === 'string' && vsme.portalUrl !== '') {
    try {
      return new URL(vsme.portalUrl, window.location.origin).pathname.replace(/\/$/, '') || '/hub';
    } catch {
      return '/hub';
    }
  }
  return '/hub';
};

export const navigateToMissionQuit = (): void => {
  const path = buildMissionQuitUrl(resolvePortalBasePath());
  window.location.assign(`${window.location.origin}${path.startsWith('/') ? path : `/${path}`}`);
};
